import React from "react";
import { Box, Grid, Typography, Paper, Skeleton } from "@mui/material";
import {
    CheckCircleOutline,
    PendingActionsOutlined,
    ReceiptLongOutlined,
    AccountBalanceWalletOutlined,
} from "@mui/icons-material";
import { twMerge } from "tailwind-merge";
import { useNavigate } from "react-router-dom";

const formatCurrency = (val) =>
    `Rs. ${Number(val ?? 0).toLocaleString("en-IN")}`;

const VendorPaymentChargeOverVIew = ({ statsData, isLoading }) => {
    const navigate = useNavigate();

    const totalCODPaid = statsData?.totalCODPaid ?? 0;
    const totalCODNotPaid = statsData?.totalCODNotPaid ?? 0;
    const totalChargePaid = statsData?.totalChargePaid ?? 0;
    const totalChargeNotPaid = statsData?.totalChargeNotPaid ?? 0;

    const totalCOD = totalCODPaid + totalCODNotPaid;
    const codPaidPct = totalCOD ? Math.round((totalCODPaid / totalCOD) * 100) : 0;
    const codPendingPct = totalCOD ? 100 - codPaidPct : 0;

    const totalCharge = totalChargePaid + totalChargeNotPaid;
    const chargePaidPct = totalCharge ? Math.round((totalChargePaid / totalCharge) * 100) : 0;
    const chargeNotPaidPct = totalCharge ? 100 - chargePaidPct : 0;

    const summaryCards = [
        {
            label: "COD paid",
            value: formatCurrency(totalCODPaid),
            icon: <CheckCircleOutline fontSize="large" className="text-emerald-500" />,
            bg: "bg-emerald-50 dark:bg-emerald-900",
            tab: "cod-paid",
        },
        {
            label: "COD not paid",
            value: formatCurrency(totalCODNotPaid),
            icon: <PendingActionsOutlined fontSize="large" className="text-amber-500" />,
            bg: "bg-amber-50 dark:bg-amber-900",
            tab: "cod-unpaid",
        },
        {
            label: "Charge paid",
            value: formatCurrency(totalChargePaid),
            icon: <ReceiptLongOutlined fontSize="large" className="text-blue-500" />,
            bg: "bg-blue-50 dark:bg-blue-900",
            tab: "charge-paid",
        },
        {
            label: "Charge not paid",
            value: formatCurrency(totalChargeNotPaid),
            icon: <AccountBalanceWalletOutlined fontSize="large" className="text-rose-500" />,
            bg: "bg-rose-50 dark:bg-rose-900",
            tab: "charge-unpaid",
        },
    ];

    if (isLoading) {
        return (
            <Grid container spacing={2}>
                {[1, 2, 3, 4].map((i) => (
                    <Grid key={i} item xs={12} sm={6} md={3}>
                        <Skeleton variant="rounded" height={110} sx={{ borderRadius: 4 }} />
                    </Grid>
                ))}
            </Grid>
        );
    }

    return (
        <Box>
            <Grid container spacing={2}>
                {summaryCards.map((card) => (
                    <Grid
                        key={card.label}
                        item
                        xs={12}
                        sm={6}
                        md={3}
                        sx={{
                            transition: "transform 0.2s ease",
                            "&:hover": { transform: "scale(1.05)", cursor: "pointer" },
                        }}
                        // ← clicking card navigates to detail page with tab pre-selected
                        onClick={() =>
                            navigate(`/app/vendor/payment-details?tab=${card.tab}`)
                        }
                    >
                        <Paper
                            className={twMerge(
                                "overflow-hidden shadow-md transition-all duration-300 p-3",
                                card.bg
                            )}
                            sx={{ borderRadius: 4, height: "100%" }}
                        >
                            <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                                mb={1}
                            >
                                <Typography variant="subtitle1" color="textSecondary" noWrap>
                                    {card.label}
                                </Typography>
                                {card.icon}
                            </Box>
                            <Typography
                                variant="h5"
                                fontWeight="bold"
                                className="text-gray-900 dark:text-white"
                                noWrap
                            >
                                {card.value}
                            </Typography>
                        </Paper>
                    </Grid>
                ))}
            </Grid>

            {(totalCOD > 0 || totalCharge > 0) && (
                <Paper
                    className="p-4 mt-4 shadow-md dark:bg-gray-900"
                    sx={{ borderRadius: 4 }}
                >
                    <Typography
                        variant="h6"
                        fontWeight="bold"
                        className="text-gray-900 dark:text-white"
                        mb={2}
                    >
                        Payment status
                    </Typography>

                    <Box display="flex" gap={4} flexWrap="wrap">
                        {/* COD side */}
                        {totalCOD > 0 && (
                            <Box flex={1} minWidth={200}>
                                <Typography variant="body2" color="textSecondary" mb={1}>
                                    COD payment
                                </Typography>
                                <Box sx={{ display: "flex", height: 10, borderRadius: 999, overflow: "hidden" }}>
                                    <Box sx={{ width: `${codPaidPct}%`, bgcolor: "#10b981", transition: "width 0.4s ease" }} />
                                    <Box sx={{ width: `${codPendingPct}%`, bgcolor: "#f59e0b", transition: "width 0.4s ease" }} />
                                </Box>
                                <Box display="flex" justifyContent="space-between" mt={1}>
                                    <Box display="flex" alignItems="center" gap={0.75}>
                                        <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#10b981" }} />
                                        <Typography variant="caption" color="textSecondary">Paid — {codPaidPct}%</Typography>
                                    </Box>
                                    <Box display="flex" alignItems="center" gap={0.75}>
                                        <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#f59e0b" }} />
                                        <Typography variant="caption" color="textSecondary">Not paid — {codPendingPct}%</Typography>
                                    </Box>
                                </Box>
                            </Box>
                        )}

                        {/* Divider */}
                        {totalCOD > 0 && totalCharge > 0 && (
                            <Box sx={{ width: "1px", bgcolor: "divider", alignSelf: "stretch" }} />
                        )}

                        {/* Charge side */}
                        {totalCharge > 0 && (
                            <Box flex={1} minWidth={200}>
                                <Typography variant="body2" color="textSecondary" mb={1}>
                                    Charge payment
                                </Typography>
                                <Box sx={{ display: "flex", height: 10, borderRadius: 999, overflow: "hidden" }}>
                                    <Box sx={{ width: `${chargePaidPct}%`, bgcolor: "#3b82f6", transition: "width 0.4s ease" }} />
                                    <Box sx={{ width: `${chargeNotPaidPct}%`, bgcolor: "#f43f5e", transition: "width 0.4s ease" }} />
                                </Box>
                                <Box display="flex" justifyContent="space-between" mt={1}>
                                    <Box display="flex" alignItems="center" gap={0.75}>
                                        <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#3b82f6" }} />
                                        <Typography variant="caption" color="textSecondary">Paid — {chargePaidPct}%</Typography>
                                    </Box>
                                    <Box display="flex" alignItems="center" gap={0.75}>
                                        <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#f43f5e" }} />
                                        <Typography variant="caption" color="textSecondary">Not paid — {chargeNotPaidPct}%</Typography>
                                    </Box>
                                </Box>
                            </Box>
                        )}
                    </Box>
                </Paper>
            )}
        </Box>
    );
};

export default VendorPaymentChargeOverVIew;